import { mdiChevronRight, mdiLockOutline } from '@mdi/js';
import _ from 'lodash';
import React, { useState } from 'react';
import {
    ProviderGuarantor,
    ProviderPayer,
    ProviderPayerSubscriber,
    ProviderSubscriber,
    ProviderSubscriberPatient,
    SupportGuarantor,
    SupportPayer,
    SupportPayerSubscriber,
    SupportSubscriber,
    SupportSubscriberPatient,
} from '../../../api';
import { LoadingMeter, Spinner } from '../../base';
import { CardAction } from '../cards/cards';
import { ClaimOverviewSectionMenu } from './claim-overview-section-menu';
import * as S from './claim-overview.styles';
import * as SectionS from '../claim-section.styles';
import {
    formatAddress,
    formatGuarantorName,
    formatPatientDemographics,
    formatPayerTitle,
    formatPersonName,
    formatPhone,
    formatRelationshipLabel,
} from './claim-overview.utils';

const PAYER_ROLES = ['primary', 'secondary', 'tertiary'] as const;
type PayerRole = (typeof PAYER_ROLES)[number];

export type ClaimOverviewSection = 'patient' | PayerRole | 'guarantor';
export type ClaimOverviewSectionActions = Partial<Record<ClaimOverviewSection, CardAction[]>>;

export interface IClaimOverviewProps {
    loading: boolean;
    guarantor?: ProviderGuarantor | SupportGuarantor;
    subscriberPatient?: ProviderSubscriberPatient | SupportSubscriberPatient;
    payerSubscribers?: ProviderPayerSubscriber[] | SupportPayerSubscriber[];
    payers?: ProviderPayer[] | SupportPayer[];
    subscribers?: ProviderSubscriber[] | SupportSubscriber[];
    thirdPartyAdministrators?: Array<ProviderPayer | SupportPayer | undefined>;
    onMakePayment?: () => Promise<void>;
    viewFullDetailsHref?: string;
    onViewFullDetails?: () => void;
    embedded?: boolean;
    sectionActions?: ClaimOverviewSectionActions;
}

const getPayerAtIndex = (
    index: number,
    payerSubscribers?: ProviderPayerSubscriber[] | SupportPayerSubscriber[],
    payers?: ProviderPayer[] | SupportPayer[],
): ProviderPayer | SupportPayer | undefined => {
    const payerSubscriber = _.get(payerSubscribers, index);
    if (!payerSubscriber) {
        return;
    }

    return _.find(payers, { id: _.get(payerSubscriber, 'payer.id') }) || payerSubscriber.payer;
};

export const ClaimOverview: React.FC<IClaimOverviewProps> = ({
    loading,
    guarantor,
    subscriberPatient,
    payerSubscribers,
    payers,
    thirdPartyAdministrators,
    onMakePayment,
    viewFullDetailsHref,
    onViewFullDetails,
    embedded = false,
    sectionActions,
}) => {
    const [paymentLoading, setPaymentLoading] = useState(false);
    const patient = subscriberPatient?.patient;

    const handleMakePayment = async (): Promise<void> => {
        if (!onMakePayment) {
            return;
        }

        setPaymentLoading(true);
        try {
            await onMakePayment();
        } finally {
            setPaymentLoading(false);
        }
    };

    const renderViewFullDetails = (): React.ReactNode => {
        const label = (
            <>
                View full details <S.ChevronIcon path={mdiChevronRight} />
            </>
        );

        if (viewFullDetailsHref) {
            return <S.ViewDetailsLink to={viewFullDetailsHref}>{() => label}</S.ViewDetailsLink>;
        }

        if (onViewFullDetails) {
            return (
                <S.ViewDetailsButton type="button" variant="unstyled" onClick={onViewFullDetails}>
                    {label}
                </S.ViewDetailsButton>
            );
        }

        return null;
    };

    const renderSectionMenu = (section: ClaimOverviewSection): React.ReactNode => {
        const actions = sectionActions?.[section];
        if (!actions?.length) {
            return null;
        }

        return <ClaimOverviewSectionMenu actions={actions} />;
    };

    const renderField = (label: string, value?: React.ReactNode, emphasized?: boolean): React.ReactElement => (
        <S.Field>
            <S.FieldLabel>{label}</S.FieldLabel>
            {value ? <S.FieldValue $emphasized={emphasized}>{value}</S.FieldValue> : <S.EmptyValue>Not on file</S.EmptyValue>}
        </S.Field>
    );

    const renderPatientSection = (): React.ReactElement => {
        const patientName = patient ? formatPersonName(patient.first, patient.last, patient.suffix) : undefined;

        return (
            <S.Section $first>
                <S.SectionHeader>
                    <S.SectionLabel>Patient</S.SectionLabel>
                    {renderSectionMenu('patient')}
                </S.SectionHeader>
                <S.NameRow>
                    <S.Name>{patientName || 'Not on file'}</S.Name>
                    {onMakePayment && (
                        <S.MakePaymentButton variant="primary" size="small" onClick={handleMakePayment} disabled={paymentLoading}>
                            {paymentLoading ? (
                                <Spinner size="16px" />
                            ) : (
                                <>
                                    <S.LockIcon path={mdiLockOutline} />
                                    Make Payment
                                </>
                            )}
                        </S.MakePaymentButton>
                    )}
                </S.NameRow>
                {patient && <S.Demographics>{formatPatientDemographics(patient)}</S.Demographics>}
                {renderField('Patient ID', patient?.externalId || patient?.id)}
                {renderField('Address', formatAddress(patient?.addresses))}
            </S.Section>
        );
    };

    const renderPayerSection = (role: PayerRole, index: number): React.ReactElement => {
        const payerSubscriber = _.get(payerSubscribers, index);
        const payer = getPayerAtIndex(index, payerSubscribers, payers);
        const thirdPartyAdministrator = thirdPartyAdministrators?.[index];
        const hasPayer = Boolean(payerSubscriber && payer);

        return (
            <S.Section key={role}>
                <S.SectionHeader>
                    <S.RoleBadge $variant={role}>{role}</S.RoleBadge>
                    {renderSectionMenu(role)}
                </S.SectionHeader>
                {hasPayer ? (
                    <>
                        <S.Name>{formatPayerTitle(payer)}</S.Name>
                        {payer?.description && <S.Subtitle>{payer.description}</S.Subtitle>}
                        {renderField('TPA', thirdPartyAdministrator?.name || payer?.name)}
                        {renderField('Member ID', payerSubscriber?.memberId, true)}
                    </>
                ) : (
                    <S.EmptyValue>Not on file</S.EmptyValue>
                )}
            </S.Section>
        );
    };

    const renderGuarantorSection = (): React.ReactElement => {
        const guarantorName = formatGuarantorName(guarantor);
        const relationship = formatRelationshipLabel(subscriberPatient);
        const phone = formatPhone(guarantor?.phone);

        return (
            <S.Section>
                <S.SectionHeader>
                    <S.RoleBadge $variant="guarantor">Guarantor</S.RoleBadge>
                    {renderSectionMenu('guarantor')}
                </S.SectionHeader>
                {guarantor ? (
                    <>
                        <S.Name>{guarantorName}</S.Name>
                        {relationship && <S.Subtitle>{relationship}</S.Subtitle>}
                        {renderField('Address', formatAddress(guarantor.addresses))}
                        <S.Field>
                            <S.FieldLabel>Phone</S.FieldLabel>
                            {phone ? (
                                <S.FieldValue>{phone}</S.FieldValue>
                            ) : (
                                <S.EmptyValue>No phone on file</S.EmptyValue>
                            )}
                        </S.Field>
                    </>
                ) : (
                    <S.EmptyValue>Not on file</S.EmptyValue>
                )}
            </S.Section>
        );
    };

    const body = loading ? (
        <S.Loader>
            <LoadingMeter />
        </S.Loader>
    ) : (
        <S.Content>
            {renderPatientSection()}
            {PAYER_ROLES.map((role, index) => renderPayerSection(role, index))}
            {renderGuarantorSection()}
        </S.Content>
    );

    if (embedded) {
        return (
            <SectionS.ClaimSectionBlock>
                <SectionS.ClaimSectionHeader>
                    <SectionS.ClaimSectionTitle>Claim Overview</SectionS.ClaimSectionTitle>
                    {renderViewFullDetails()}
                </SectionS.ClaimSectionHeader>
                {body}
            </SectionS.ClaimSectionBlock>
        );
    }

    return (
        <S.StyledTitleCard size="large" title="Claim Overview" caption={renderViewFullDetails()}>
            {body}
        </S.StyledTitleCard>
    );
};
